import * as Sentry from '@sentry/react'
import { useState, useCallback, useRef } from 'react'
import type { ChatMessage } from '../../shared/types/chat'
import type { NewMessagePayload } from './useRealtimeEvents'
import type { AssistantMessageData, SseEvent, ApiError } from './api'
import { formatDisplayTime } from '@tendbot/ui'
import { buildMessagesFromResponse } from './utils'
import * as api from './api'

export interface StreamingMessage {
  messageId: string
  conversationId: string
  content: string
  toolName: string | null
}

interface UseStreamingChatParams {
  conversationId: string | null
  setConversationId: React.Dispatch<React.SetStateAction<string | null>>
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
  tryDecline: (content: string) => boolean
  updateConversationPreview: (id: string, lastMessage: string, updatedAt: string) => void
  updateConversationTitle: (id: string, title: string) => void
  t: (key: string) => string
  onTierBlocked?: (code: string) => void
}

export interface UseStreamingChatResult {
  isTyping: boolean
  streamingMessage: StreamingMessage | null
  streamCompletedIds: React.MutableRefObject<Set<string>>
  handleSendMessage: (content: string, hint?: string, imageIds?: string[], imageUrls?: string[]) => Promise<void>
  handleNewMessage: (payload: NewMessagePayload) => void
}

export function useStreamingChat({
  conversationId,
  setConversationId,
  setMessages,
  tryDecline,
  updateConversationPreview,
  updateConversationTitle,
  t,
  onTierBlocked,
}: UseStreamingChatParams): UseStreamingChatResult {
  const [isTyping, setIsTyping] = useState(false)
  const [streamingMessage, setStreamingMessage] = useState<StreamingMessage | null>(null)
  const streamContentRef = useRef('')
  const streamCompletedIds = useRef<Set<string>>(new Set())

  // Handle async-inserted messages (e.g. briefing cards, pipeline emails) that arrive outside the streaming pipeline
  const handleNewMessage = useCallback((payload: NewMessagePayload) => {
    // Always update sidebar preview so conversation list stays current
    if (payload.role === 'assistant' || !payload.role) {
      const preview = payload.content?.slice(0, 100) ?? ''
      updateConversationPreview(payload.conversationId, preview, payload.timestamp)
    }

    // Only add to message list if this is the active conversation
    if (conversationId && payload.conversationId !== conversationId) return

    const msg: ChatMessage = {
      id: payload.messageId,
      type: payload.role === 'user' ? 'user' : 'assistant',
      content: payload.content,
      timestamp: payload.timestamp,
      displayTime: formatDisplayTime(payload.timestamp),
      ...(payload.briefingCard && { briefingCard: payload.briefingCard as unknown as ChatMessage['briefingCard'] }),
      ...(payload.triageEmail && { triageEmail: payload.triageEmail as unknown as ChatMessage['triageEmail'] }),
      ...(payload.sourceEmailId && { sourceEmailId: payload.sourceEmailId }),
    }
    streamCompletedIds.current.add(msg.id)
    setMessages(prev => {
      if (prev.some(m => m.id === payload.messageId)) return prev
      return [...prev, msg]
    })
  }, [conversationId, setMessages, updateConversationPreview])

  /** Process a single SSE event from the streaming response. */
  const handleSseEvent = useCallback((sseEvent: SseEvent, trackingConversationId: string, retryContent?: string) => {
    const { event, data } = sseEvent

    switch (event) {
      case 'meta':
        if (!conversationId && data.conversationId) {
          setConversationId(data.conversationId as string)
        }
        break

      case 'delta': {
        const delta = data.delta as string
        streamContentRef.current += delta
        setStreamingMessage({
          messageId: data.messageId as string || '',
          conversationId: trackingConversationId,
          content: streamContentRef.current,
          toolName: null,
        })
        break
      }

      case 'tool_activity':
        // A tool call is starting — any text streamed so far was mid-loop narration
        // ("let me check…") that will be superseded by the final turn's answer.
        // Clear the accumulated content so the user sees the tool activity indicator
        // instead of stale intermediate text.
        streamContentRef.current = ''
        setStreamingMessage(prev => ({
          messageId: prev?.messageId || '',
          conversationId: trackingConversationId,
          content: '',
          toolName: data.toolName as string,
        }))
        break

      case 'complete': {
        setIsTyping(false)
        setStreamingMessage(null)
        streamContentRef.current = ''

        const newMessages = buildMessagesFromResponse({
          messageId: data.messageId as string,
          conversationId: data.conversationId as string,
          role: 'assistant',
          content: data.content as string,
          timestamp: data.timestamp as string,
          approvals: data.approvals as AssistantMessageData['approvals'],
          choiceOptions: data.choiceOptions as AssistantMessageData['choiceOptions'],
          multiSelect: data.multiSelect as boolean | undefined,
          sentEmail: data.sentEmail as AssistantMessageData['sentEmail'],
          pluginCards: data.pluginCards as AssistantMessageData['pluginCards'],
        })
        for (const msg of newMessages) {
          streamCompletedIds.current.add(msg.id)
        }
        setMessages(prev => {
          if (prev.some(m => m.id === data.messageId)) return prev
          return [...prev, ...newMessages]
        })

        // Update sidebar preview
        const preview = (data.content as string)?.slice(0, 100) ?? ''
        updateConversationPreview(trackingConversationId, preview, data.timestamp as string)
        break
      }

      case 'title': {
        const title = data.title as string
        const titleConvId = data.conversationId as string
        if (title && titleConvId) {
          updateConversationTitle(titleConvId, title)
        }
        break
      }

      case 'error': {
        setIsTyping(false)
        setStreamingMessage(null)
        streamContentRef.current = ''
        const retryable = data.retryable as boolean
        const errorMessage: ChatMessage = {
          id: `err-${Date.now()}`,
          type: 'assistant',
          content: retryable ? t('error.unavailable') : t('error.generic'),
          timestamp: new Date().toISOString(),
          ...(retryContent && { isRetryable: true, retryContent }),
        }
        setMessages(prev => [...prev, errorMessage])
        break
      }
    }
  }, [conversationId, setConversationId, setMessages, t, updateConversationPreview, updateConversationTitle])

  const handleSendMessage = useCallback(async (content: string, hint?: string, imageIds?: string[], imageUrls?: string[]): Promise<void> => {
    if (!content.trim() && !imageIds?.length) return

    const trimmedContent = content.trim()
    const userMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      type: 'user',
      content: trimmedContent,
      timestamp: new Date().toISOString(),
      displayTime: formatDisplayTime(),
      ...(imageIds?.length && { imageIds }),
      ...(imageUrls?.length && { imageUrls }),
    }

    setMessages(prev => [...prev, userMessage])

    if (tryDecline(content)) return

    setIsTyping(true)
    streamContentRef.current = ''

    try {
      let trackingConversationId = conversationId ?? ''
      let gotComplete = false

      for await (const sseEvent of api.sendMessageStream(content.trim(), conversationId ?? undefined, hint, imageIds)) {
        // Track conversation ID from meta event
        if (sseEvent.event === 'meta' && sseEvent.data.conversationId) {
          trackingConversationId = sseEvent.data.conversationId as string
          if (!conversationId) {
            setConversationId(trackingConversationId)
          }
        }
        if (sseEvent.event === 'complete' || sseEvent.event === 'error') {
          gotComplete = true
        }
        handleSseEvent(sseEvent, trackingConversationId, trimmedContent)
      }

      // If stream closed without a complete event, fall back to fetching messages
      if (!gotComplete) {
        setIsTyping(false)
        setStreamingMessage(null)
        streamContentRef.current = ''
        if (trackingConversationId) {
          const { messages: fetchedMessages } = await api.getMessages(trackingConversationId, undefined, 5)
          const latest = fetchedMessages[fetchedMessages.length - 1]
          if (latest && latest.role === 'assistant') {
            const latestId = latest.id
            const recovered = buildMessagesFromResponse({
              messageId: latestId,
              conversationId: trackingConversationId,
              role: 'assistant',
              content: latest.content,
              timestamp: latest.timestamp,
            })
            for (const msg of recovered) {
              streamCompletedIds.current.add(msg.id)
            }
            setMessages(prev => {
              if (prev.some(m => m.id === latestId)) return prev
              return [...prev, ...recovered]
            })
          }
        }
      }
    } catch (err) {
      console.error('Failed to send message:', err)
      setIsTyping(false)
      setStreamingMessage(null)
      streamContentRef.current = ''

      // Handle 402 (trial expired / subscription required) by showing overlay
      const apiErr = err as ApiError
      if (apiErr?.status === 402) {
        onTierBlocked?.(apiErr.code ?? 'TRIAL_EXPIRED')
        return
      }

      // Don't report transient network failures (iOS "Load failed", offline, Cloudflare 5xx)
      const status = apiErr?.status
      const isNetworkFailure = status === 0
      const isUpstream5xx = typeof status === 'number' && status >= 500 && status < 600
      if (!isNetworkFailure && !isUpstream5xx) {
        Sentry.captureException(err, { tags: { feature: 'chat' } })
      }

      const errorMessage: ChatMessage = {
        id: `err-${Date.now()}`,
        type: 'assistant',
        content: t('error.generic'),
        timestamp: new Date().toISOString(),
        isRetryable: true,
        retryContent: trimmedContent,
      }
      setMessages(prev => [...prev, errorMessage])
    }
  }, [conversationId, setConversationId, setMessages, t, tryDecline, handleSseEvent, onTierBlocked])

  return { isTyping, streamingMessage, streamCompletedIds, handleSendMessage, handleNewMessage }
}
